// ── Voice Chat settings (on the Settings page) ────────────────────────────────
// The voice chat's two choices — the voice answers are read in and how fast —
// alongside the app's other settings,
// in the same card style as Appearance. Each is saved to the account as it's
// changed (shared with the mobile app) and applies from the next answer; an
// answer already being read keeps its voice and pace.

import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiSkipForward, FiMic } from 'react-icons/fi';
import VoicePicker from './VoicePicker';
import {
  SPEED_OPTIONS,
  describeVoice,
  loadVoiceSettings,
  resolveVoiceId,
  saveVoiceSettings,
  voiceCatalogue,
  voiceSettingsSnapshot,
} from './speech/speechPrefs';
import './voicechat.css';

/** "Normal (1.0×)" → "Normal" for the toggle; the multiplier is the button's title. */
const shortSpeedLabel = (label: string) => label.replace(/\s*\(.*\)$/, '');

const VoiceChatSettingsSection: React.FC = () => {
  const [voiceName, setVoiceName] = useState('');
  const [voiceVersion, setVoiceVersion] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [speed, setSpeed] = useState(voiceSettingsSnapshot().speed);
  const [saveError, setSaveError] = useState('');

  // Fresh from the account when the page opens — a change made on the phone shows here.
  useEffect(() => {
    let cancelled = false;
    loadVoiceSettings(true).then(settings => {
      if (cancelled) return;
      setSpeed(settings.speed);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Shown at once; put back, with a message, if the account doesn't take it. */
  const save = useCallback(async (changes: { speed?: number }) => {
    setSaveError('');
    try {
      const saved = await saveVoiceSettings(changes);
      setSpeed(saved.speed);
    } catch {
      const held = voiceSettingsSnapshot();
      setSpeed(held.speed);
      setSaveError("Couldn't save that change. Please try again.");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [voices, id] = await Promise.all([voiceCatalogue(), resolveVoiceId()]);
        const voice = voices.find(v => v.voiceId === id);
        if (!cancelled) setVoiceName(voice ? describeVoice(voice).shortName : 'Not available');
      } catch {
        if (!cancelled) setVoiceName('Not available');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [voiceVersion]);

  const closePicker = useCallback((saved: boolean) => {
    setPickerOpen(false);
    if (saved) setVoiceVersion(v => v + 1);
  }, []);

  // A stored value that isn't one of the options (e.g. 1.15) shows as the nearest.
  const speedValue = SPEED_OPTIONS.reduce((best, o) => (Math.abs(o.value - speed) < Math.abs(best.value - speed) ? o : best))
    .value;

  return (
    <motion.section className="s2-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <div className="s2-section-label">
        <span className="s2-section-dot s2-section-dot--purple" />
        Voice Chat
      </div>

      <div className="s2-voice-settings">
        <div className="s2-appearance-card">
          <div className="s2-appearance-left">
            <div className="s2-appearance-icon-wrap">
              <FiMic size={20} />
            </div>
            <div>
              <div className="s2-appearance-title">Assistant voice</div>
              <div className="s2-appearance-sub">{voiceName ? `Answers are read in ${voiceName}` : 'Loading…'}</div>
            </div>
          </div>
          <button type="button" className="s2-theme-btn s2-voice-change" onClick={() => setPickerOpen(true)}>
            Change
          </button>
        </div>

        <div className="s2-appearance-card">
          <div className="s2-appearance-left">
            <div className="s2-appearance-icon-wrap">
              <FiSkipForward size={20} />
            </div>
            <div>
              <div className="s2-appearance-title">Reading speed</div>
              <div className="s2-appearance-sub">How fast answers are read aloud</div>
            </div>
          </div>
          <div className="s2-theme-toggle" role="group" aria-label="Reading speed">
            {SPEED_OPTIONS.map(o => (
              <button
                key={o.value}
                type="button"
                title={o.label}
                aria-pressed={speedValue === o.value}
                className={`s2-theme-btn${speedValue === o.value ? ' s2-theme-btn--active' : ''}`}
                onClick={() => {
                  setSpeed(o.value);
                  void save({ speed: o.value });
                }}
              >
                <span>{shortSpeedLabel(o.label)}</span>
                {speedValue === o.value && <FiCheck size={12} className="s2-theme-check" />}
              </button>
            ))}
          </div>
        </div>
      </div>

      {saveError && (
        <p className="s2-voice-error" role="alert">
          {saveError}
        </p>
      )}

      {pickerOpen && <VoicePicker onClose={closePicker} />}
    </motion.section>
  );
};

export default VoiceChatSettingsSection;
